import { Injectable } from '@angular/core';

import { AuthService } from 'app/shared/services/auth/auth.service';

interface RecentEntry {
    id: number;
    lastVisitedAt: string;
}

interface UsageStore {
    recent: RecentEntry[];
    counts: Record<number, number>;
}

const RECENT_CAP = 20;

/**
 * "Recent" and "usage frequency" tracking for menu screens - genuinely new, no existing
 * equivalent in the app (unlike "pin", which reuses FavoritePageStaffService). Scoped to the
 * real logged-in user via AraxUser.id, not the design mockup's fake role-based demo data.
 */
@Injectable({ providedIn: 'root' })
export class RecentUsageService {

    constructor(private readonly authService: AuthService) { }

    recordVisit(id: number): void {
        const store = this.load();
        store.recent = [{ id, lastVisitedAt: new Date().toISOString() }, ...store.recent.filter(r => r.id !== id)]
            .slice(0, RECENT_CAP);
        store.counts[id] = (store.counts[id] || 0) + 1;
        this.save(store);
    }

    getRecent(limit: number = 10): number[] {
        return this.load().recent.slice(0, limit).map(r => r.id);
    }

    getMostUsed(limit: number = 10): number[] {
        const counts = this.load().counts;
        return Object.keys(counts)
            .map(id => Number(id))
            .sort((a, b) => counts[b] - counts[a])
            .slice(0, limit);
    }

    private key(): string {
        const userId = this.authService.user?.id || 'anonymous';
        return `menuUsage:${userId}`;
    }

    private load(): UsageStore {
        try {
            const raw = JSON.parse(localStorage.getItem(this.key()) || 'null');
            if (raw) {
                return { recent: raw.recent || [], counts: raw.counts || {} };
            }
        } catch (e) { }
        return { recent: [], counts: {} };
    }

    private save(store: UsageStore): void {
        try {
            localStorage.setItem(this.key(), JSON.stringify(store));
        } catch (e) { }
    }
}
